import { useEffect, useState } from "react";
import { TrendingUp, ShoppingBag, Store, Pizza, ArrowUpRight, ArrowDownRight, BarChart3 } from "lucide-react";
import { api, inr } from "../../lib/api";

export default function AdminDashboard() {
    const [s, setS] = useState(null);
    const [margin, setMargin] = useState(null);
    useEffect(() => {
        api.get("/admin/dashboard").then(({ data }) => setS(data)).catch(() => {});
        api.get("/admin/margin-health").then(({ data }) => setMargin(data)).catch(() => {});
    }, []);
    const cards = [
        { label: "Revenue", v: s ? inr(s.total_revenue) : "\u2026", Icon: TrendingUp, accent: "bg-emerald-100 text-emerald-700" },
        { label: "Total Orders", v: s?.total_orders ?? "\u2026", Icon: ShoppingBag, accent: "bg-blue-100 text-blue-700" },
        { label: "Active Orders", v: s?.active_orders ?? "\u2026", Icon: ShoppingBag, accent: "bg-amber-100 text-amber-700" },
        { label: "Shops", v: s?.total_restaurants ?? "\u2026", Icon: Store, accent: "bg-violet-100 text-violet-700" },
        { label: "Products", v: s?.total_products ?? "\u2026", Icon: Pizza, accent: "bg-pink-100 text-pink-700" },
    ];

    return (
        <div>
            <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Operational overview</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                {cards.map((c) => (
                    <div key={c.label} data-testid={`metric-${c.label}`} className="bg-card border border-border rounded-2xl p-5">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.accent}`}><c.Icon size={18} /></div>
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mt-4">{c.label}</p>
                        <p className="text-2xl font-bold mt-1">{c.v}</p>
                    </div>
                ))}
            </div>

            {/* Margin Health */}
            <section className="mt-8" data-testid="margin-health">
                <div className="flex items-center gap-2">
                    <BarChart3 size={18} />
                    <h2 className="text-lg font-bold">Margin Health</h2>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Live snapshot of effective commission % and admin earn across every merchant. Based on the pricing engine, recomputed from current menu prices.</p>

                <div className="grid lg:grid-cols-[1fr_360px] gap-4 mt-4">
                    {/* Per-restaurant table */}
                    {/* overflow-x-auto, not hidden: beside the margin panels (lg grid) this card is ~320px wide at 1024px,
                        and the table's last three columns were cut off with no way to reach them. */}
                    <div className="bg-card border border-border rounded-2xl overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/60 text-[11px] uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-4 py-3">Shop</th>
                                    <th className="text-right px-4 py-3">Avg Menu</th>
                                    <th className="text-right px-4 py-3">Avg Selling</th>
                                    <th className="text-right px-4 py-3">Eff. Comm %</th>
                                    <th className="text-right px-4 py-3">Margin %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(margin?.restaurants || []).map((r) => (
                                    <tr key={r.restaurant_id} className="border-t border-border" data-testid={`mh-rest-${r.restaurant_id}`}>
                                        <td className="px-4 py-3">
                                            <p className="font-bold truncate">{r.restaurant_name}</p>
                                            <p className="text-xs text-muted-foreground">{r.products} products</p>
                                        </td>
                                        <td className="px-4 py-3 text-right">{inr(r.avg_menu_price)}</td>
                                        <td className="px-4 py-3 text-right font-semibold">{inr(r.avg_selling_price)}</td>
                                        <td className="px-4 py-3 text-right">{r.effective_commission_pct}%</td>
                                        <td className={`px-4 py-3 text-right font-bold ${r.margin_pct >= 20 ? "text-emerald-600" : r.margin_pct >= 10 ? "text-amber-600" : "text-red-600"}`}>{r.margin_pct}%</td>
                                    </tr>
                                ))}
                                {!margin && (
                                    <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-sm">Loading…</td></tr>
                                )}
                                {margin && margin.restaurants?.length === 0 && (
                                    <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-sm">No restaurants with priced products yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Top / bottom products */}
                    <div className="space-y-4">
                        <ProductsCard label="Highest margin" Icon={ArrowUpRight} accent="text-emerald-600 bg-emerald-50" items={margin?.top_margin_products} />
                        <ProductsCard label="Lowest margin" Icon={ArrowDownRight} accent="text-red-600 bg-red-50" items={margin?.bottom_margin_products} />
                    </div>
                </div>
            </section>
        </div>
    );
}

function ProductsCard({ label, Icon, accent, items }) {
    return (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className={`px-4 py-3 flex items-center gap-2 ${accent}`}>
                <Icon size={16} />
                <p className="font-bold text-sm">{label}</p>
            </div>
            <div className="divide-y divide-border">
                {(items || []).map((p) => (
                    <div key={p.product_id} className="px-4 py-3 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate">{p.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{p.restaurant_name}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-muted-foreground">Sell {inr(p.selling_price)}</p>
                            <p className="font-bold text-sm">{p.margin_pct}%</p>
                        </div>
                    </div>
                ))}
                {!items && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
                {items && items.length === 0 && <div className="p-4 text-xs text-muted-foreground">No data yet.</div>}
            </div>
        </div>
    );
}
